import { apiClient } from "../api/apiClient.js";

// Thin fetch wrappers over the P154 Backoffice Chat API
// (BackofficeChatController/ChatService) - the Agent's half of the worker's
// messaging screen. Same base/auth convention as ../sos/sosApi.js: the same
// Bearer-session-authenticated backend every other backoffice controller uses.
//
// There is deliberately no edit and no delete wrapper here, because the
// backend deliberately exposes no edit and no delete route. A worker writes
// in this thread about pay, injuries and conditions; the party those
// complaints are about must not be able to make them disappear. The only
// post-insert write in this whole feature is marking a message read, and the
// backend does that itself when the thread is opened - there is no
// acknowledge call to forget.

const CHAT_PATH = "/backoffice/chat/threads";

// Exactly the query param names BackofficeChatController.ListThreads
// declares, in the same order as the controller signature - anything else
// passed to listChatThreads is silently dropped rather than forwarded.
// Scope-isolated visibility always happens on the backend from the caller's
// own session (ChatService.ResolveAgentScope), never from a value this file
// sends: an Agent id is never constructed here.
const THREADS_LIST_PARAM_KEYS = ["search", "pageSize"];

// Exactly the query param names BackofficeChatController.GetThread declares.
const THREAD_PARAM_KEYS = ["afterSentAtUtc", "pageSize"];

/** Builds a query string with every empty/null/undefined value omitted entirely (never sent as `key=`), restricted to `paramKeys`. */
function buildQuery(paramKeys, params = {}) {
  const searchParams = new URLSearchParams();
  for (const key of paramKeys) {
    const value = params[key];
    if (value === null || value === undefined || value === "") {
      continue;
    }
    searchParams.set(key, String(value));
  }
  const query = searchParams.toString();
  return query ? `?${query}` : "";
}

export function listChatThreads(params = {}) {
  return apiClient.get(`${CHAT_PATH}${buildQuery(THREADS_LIST_PARAM_KEYS, params)}`);
}

// Reading a thread is what marks the worker's messages read - that happens
// server-side inside this same GET, so a caller that fetches a thread has
// already acknowledged it and should refresh the inbox to clear the badge.
export function getChatThread(threadId, params = {}) {
  if (!threadId) {
    throw new Error("getChatThread requires a threadId");
  }
  return apiClient.get(`${CHAT_PATH}/${encodeURIComponent(threadId)}${buildQuery(THREAD_PARAM_KEYS, params)}`);
}

// The worker this reply reaches comes from the thread on the server, never
// from anything this file sends - the request body carries the text alone.
export function sendChatReply(threadId, body) {
  if (!threadId) {
    throw new Error("sendChatReply requires a threadId");
  }
  return apiClient.post(`${CHAT_PATH}/${encodeURIComponent(threadId)}/messages`, { body });
}
